import { db } from './config';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  type DocumentReference,
  type FirestoreError,
  type Unsubscribe,
} from 'firebase/firestore';
import type { ClinicalRecord } from '@/types/models';
import { stripUndefinedDeep } from './utils';
import { recordEventDate } from '@/utils/recordDate';

export interface NewClinicalRecordInput {
  professionalId: string;
  appointmentId?: string;
  type: ClinicalRecord['type'];
  content: Record<string, unknown>;
  attachments?: ClinicalRecord['attachments'];
  source?: 'HUMAN' | 'AI_ASSISTED';
  aiModel?: string;
  /**
   * Rótulo de categoria de topo, indexável. Hoje só usamos 'IMAGE' para
   * registros que são apenas imagens anexadas — permite que a secretária
   * (com permissão de "Imagens da ficha") crie/veja essas entradas sem
   * acessar o resto do prontuário. Ver firestore.rules → records.
   */
  category?: 'IMAGE';
  /** Rótulo de exibição na coluna do prontuário (ex.: "Recado do chat"). */
  displayTitle?: string;
}

/**
 * Monta a referência e o documento exatamente como `createRecord` grava —
 * pra quem precisa escrever numa `writeBatch` (ex.: registro do ditado,
 * spec 009) sem duplicar o formato do registro.
 */
export function buildClinicalRecord(
  clinicId: string,
  patientId: string,
  data: NewClinicalRecordInput,
): { ref: DocumentReference; record: Record<string, unknown> } {
  const ref = doc(collection(db, 'clinics', clinicId, 'patients', patientId, 'records'));
  // IMPORTANTE: Firestore rejeita campos com valor `undefined` (Function setDoc()
  // called with invalid data. Unsupported field value: undefined). Por isso construimos
  // um Record<string, unknown> e filtramos undefined antes de mandar pro setDoc.
  const rawRecord: Record<string, unknown> = {
    id: ref.id,
    patientId,
    clinicId,
    professionalId: data.professionalId,
    appointmentId: data.appointmentId,
    type: data.type,
    // Limpeza PROFUNDA: `content` é aninhado (ex.: encounterExams.aiFilled
    // pode vir `undefined`) e o Firestore rejeita `undefined` em qualquer
    // nível — o filtro raso abaixo só cobriria o topo.
    content: stripUndefinedDeep(data.content),
    category: data.category,
    displayTitle: data.displayTitle?.trim() || undefined,
    attachments: data.attachments ?? [],
    version: 1,
    source: data.source ?? 'HUMAN',
    aiGenerated: data.source === 'AI_ASSISTED',
    aiModel: data.aiModel,
    updatedBy: data.professionalId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const record = Object.fromEntries(
    Object.entries(rawRecord).filter(([, v]) => v !== undefined),
  );
  return { ref, record };
}

export const ClinicalRecordService = {
  async createRecord(
    clinicId: string,
    patientId: string,
    data: NewClinicalRecordInput,
  ): Promise<ClinicalRecord> {
    const { ref, record } = buildClinicalRecord(clinicId, patientId, data);
    await setDoc(ref, record);
    return record as unknown as ClinicalRecord;
  },

  /**
   * T078: Update with auto-versioning.
   * Saves current state to versions/ subcollection before updating.
   * Stores timestamp, author, and full previous content for diff.
   */
  async updateRecord(
    clinicId: string,
    patientId: string,
    recordId: string,
    updatedBy: string,
    updates: { content?: Record<string, unknown>; attachments?: ClinicalRecord['attachments'] },
  ): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId, 'records', recordId);
    const currentSnap = await getDoc(docRef);

    if (!currentSnap.exists()) {
      throw new Error(`Record ${recordId} not found`);
    }

    const currentData = currentSnap.data() as ClinicalRecord;

    // Archive current version
    const versionRef = doc(collection(docRef, 'versions'));
    await setDoc(versionRef, {
      ...currentData,
      versionNumber: currentData.version,
      archivedAt: serverTimestamp(),
      archivedBy: updatedBy,
    });

    // Update the main document with new version
    await updateDoc(docRef, {
      ...(updates.content !== undefined ? { content: stripUndefinedDeep(updates.content) } : {}),
      ...(updates.attachments !== undefined ? { attachments: updates.attachments } : {}),
      version: currentData.version + 1,
      updatedBy,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Define (ou limpa, com string vazia) o rótulo de exibição do registro na
   * coluna do prontuário. Update MÍNIMO: toca só em `displayTitle` — por isso
   * NÃO arquiva versão nem mexe em `content`/`version`. A regra do Firestore
   * `isDisplayTitleUpdate()` libera esse update mesmo após as 24h (renomear um
   * registro antigo é organização, não edição de conteúdo clínico).
   */
  async setDisplayTitle(
    clinicId: string,
    patientId: string,
    recordId: string,
    title: string,
  ): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId, 'records', recordId);
    await updateDoc(docRef, { displayTitle: title.trim() });
  },

  /**
   * Get all versions of a record for diff/audit.
   */
  async getVersionHistory(
    clinicId: string,
    patientId: string,
    recordId: string,
  ) {
    const versionsRef = collection(
      db,
      'clinics',
      clinicId,
      'patients',
      patientId,
      'records',
      recordId,
      'versions',
    );
    const q = query(versionsRef, orderBy('versionNumber', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => d.data());
  },

  async getRecord(clinicId: string, patientId: string, recordId: string) {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId, 'records', recordId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return snap.data() as ClinicalRecord;
  },

  async deleteRecord(clinicId: string, patientId: string, recordId: string): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'patients', patientId, 'records', recordId);
    await deleteDoc(docRef);
  },

  async listRecords(clinicId: string, patientId: string) {
    const q = query(
      collection(db, 'clinics', clinicId, 'patients', patientId, 'records'),
      orderBy('createdAt', 'desc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => d.data() as ClinicalRecord);
  },

  /**
   * `listRecords` AO VIVO: `callback` recebe a lista inteira na primeira
   * leitura e de novo a cada registro gravado, editado ou excluído — venha de
   * onde vier (ditado, chat, outra máquina). O `createdAt` ainda pendente no
   * servidor vem estimado, pra o registro recém-gravado já entrar no lugar
   * certo da lista.
   */
  subscribeRecords(
    clinicId: string,
    patientId: string,
    callback: (records: ClinicalRecord[]) => void,
    onError?: (err: FirestoreError) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, 'clinics', clinicId, 'patients', patientId, 'records'),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(
      q,
      (snap) =>
        callback(snap.docs.map((d) => d.data({ serverTimestamps: 'estimate' }) as ClinicalRecord)),
      (err) => onError?.(err),
    );
  },

  /**
   * Registros criados a partir de `sinceMs` (epoch ms), mais novo primeiro.
   * Um filtro de faixa num campo só — sem índice composto. `createdAt` é
   * gravado com `serverTimestamp()`, então a faixa vai como Timestamp. É pra
   * achar o que foi anexado HOJE (foto da tonometria do ditado, spec 009);
   * os migrados do HiDoctor têm createdAt = dia da migração e não casam.
   */
  async listRecordsSince(clinicId: string, patientId: string, sinceMs: number) {
    const q = query(
      collection(db, 'clinics', clinicId, 'patients', patientId, 'records'),
      where('createdAt', '>=', Timestamp.fromMillis(sinceMs)),
      orderBy('createdAt', 'desc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => d.data() as ClinicalRecord);
  },

  /**
   * Lista SÓ os registros de imagem (`category == 'IMAGE'`). Filtro de
   * igualdade única — não exige índice composto e casa com a regra do
   * Firestore que libera a secretária (permissão 'clinicalImages') a ler
   * apenas imagens. Ordena por data no cliente (desc).
   */
  async listImageRecords(clinicId: string, patientId: string) {
    const q = query(
      collection(db, 'clinics', clinicId, 'patients', patientId, 'records'),
      where('category', '==', 'IMAGE'),
    );
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map((d) => d.data() as ClinicalRecord);
    // Data do exame (recordDate) quando houver — migrados e OCT automático.
    return list.sort((a, b) => recordEventDate(b) - recordEventDate(a));
  },
};
